import { useState, useEffect } from 'react';
import {
  Receipt, Save, Loader2, AlertCircle, CheckCircle,
  Banknote, Mail, MapPin, Phone, Globe, FileText, Clock, Package,
} from 'lucide-react';
import { authenticatedFetch, getUser } from '@/lib/auth';

/**
 * Fakturaoppsett (kun synlig for systemadmin).
 *
 * Styrer alt som rendres på utgående KI-topup-fakturaer:
 *   - Avsender-identitet (navn, org-nr, adresse, kontakt)
 *   - Betalingskonto og finance-e-post (BCC-mottaker)
 *   - Pris + pakkestørrelse + forfallsdager + fri-tekstmerknad
 *
 * Skriver til system_settings.invoicing via PUT /api/admin/system-settings/invoicing
 * (merger inn — delvis oppdatering er trygt).
 */

interface InvoicingSettingsValue {
  sender_name?: string;
  sender_org_number?: string;
  sender_address_line1?: string;
  sender_address_line2?: string;
  sender_email?: string;
  sender_phone?: string;
  sender_website?: string;
  finance_email?: string;
  bank_account?: string;
  default_due_days?: number;
  topup_price_nok?: number;
  topup_tokens_per_invoice?: number;
  notes_on_invoice?: string;
  is_vat_registered?: boolean;
  kid_strategy?: string;
  kid_prefix?: string;
  updated_at?: string;
}

export default function InvoicingSettings() {
  const currentUser = getUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [serverUpdatedAt, setServerUpdatedAt] = useState<string | null>(null);
  const [value, setValue] = useState<InvoicingSettingsValue>({});

  useEffect(() => {
    if (currentUser?.role === 'systemadmin') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const r = await authenticatedFetch('/api/admin/system-settings/invoicing');
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setValue(data.value || {});
      setServerUpdatedAt(data.updated_at || null);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste fakturaoppsett');
    } finally {
      setLoading(false);
    }
  }

  function set<K extends keyof InvoicingSettingsValue>(key: K, v: InvoicingSettingsValue[K]) {
    setValue(prev => ({ ...prev, [key]: v }));
    setSuccess('');
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload: InvoicingSettingsValue = {
        sender_name: value.sender_name?.trim() || '',
        sender_org_number: value.sender_org_number?.trim() || '',
        sender_address_line1: value.sender_address_line1?.trim() || '',
        sender_address_line2: value.sender_address_line2?.trim() || '',
        sender_email: value.sender_email?.trim() || '',
        sender_phone: value.sender_phone?.trim() || '',
        sender_website: value.sender_website?.trim() || '',
        finance_email: value.finance_email?.trim() || '',
        bank_account: value.bank_account?.trim() || '',
        default_due_days: Number(value.default_due_days) || 30,
        topup_price_nok: Number(value.topup_price_nok) || 150,
        topup_tokens_per_invoice: Number(value.topup_tokens_per_invoice) || 1_000_000,
        notes_on_invoice: value.notes_on_invoice || '',
      };

      if (!payload.sender_name || !payload.sender_org_number) {
        throw new Error('Avsendernavn og organisasjonsnummer er påkrevd');
      }
      if (!payload.bank_account) {
        throw new Error('Bankkonto er påkrevd — faktura kan ikke sendes uten');
      }
      if (!payload.finance_email) {
        throw new Error('Finance-e-post er påkrevd (brukes som BCC-mottaker)');
      }
      if (payload.default_due_days! < 1 || payload.default_due_days! > 180) {
        throw new Error('Forfallsdager må være mellom 1 og 180');
      }
      if (payload.topup_price_nok! <= 0) {
        throw new Error('Pris per pakke må være høyere enn 0');
      }

      const r = await authenticatedFetch('/api/admin/system-settings/invoicing', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: payload }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setValue(data.value);
      setServerUpdatedAt(data.updated_at);
      setSuccess('Fakturaoppsett lagret');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke lagre');
    } finally {
      setSaving(false);
    }
  }

  if (currentUser?.role !== 'systemadmin') {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Laster fakturaoppsett…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <Receipt className="h-6 w-6 text-kf-primary" />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Fakturaoppsett</h2>
            <p className="text-sm text-gray-600 mt-1">
              Avsender-identitet, bankkonto og pris som brukes på utgående KI-fakturaer.
              Endringer gjelder umiddelbart for alle fremtidige fakturaer.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-400 flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-8">
          {/* Avsender */}
          <section>
            <h3 className="text-sm font-semibold uppercase text-gray-500 tracking-wide mb-3">
              Avsender på fakturaen
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Avsendernavn" required>
                <input type="text" className={inputCls}
                  value={value.sender_name || ''}
                  onChange={e => set('sender_name', e.target.value)}
                  placeholder="Kompetanseforbundet" />
              </Field>
              <Field label="Organisasjonsnummer" required>
                <input type="text" className={inputCls}
                  value={value.sender_org_number || ''}
                  onChange={e => set('sender_org_number', e.target.value)}
                  placeholder="123 456 789" />
              </Field>
              <Field label="Adresselinje 1" icon={<MapPin size={16} />}>
                <input type="text" className={inputCls + ' pl-9'}
                  value={value.sender_address_line1 || ''}
                  onChange={e => set('sender_address_line1', e.target.value)}
                  placeholder="Løkkeveien 7a" />
              </Field>
              <Field label="Adresselinje 2 (postnr + sted)">
                <input type="text" className={inputCls}
                  value={value.sender_address_line2 || ''}
                  onChange={e => set('sender_address_line2', e.target.value)}
                  placeholder="0253 Oslo" />
              </Field>
              <Field label="Kontakt e-post (synlig på faktura)" icon={<Mail size={16} />}>
                <input type="email" className={inputCls + ' pl-9'}
                  value={value.sender_email || ''}
                  onChange={e => set('sender_email', e.target.value)}
                  placeholder="postmottak@kompetanseforbundet.no" />
              </Field>
              <Field label="Kontakt telefon" icon={<Phone size={16} />}>
                <input type="tel" className={inputCls + ' pl-9'}
                  value={value.sender_phone || ''}
                  onChange={e => set('sender_phone', e.target.value)}
                  placeholder="+47 222 31 400" />
              </Field>
              <Field label="Nettside (vises i footer)" icon={<Globe size={16} />}>
                <input type="text" className={inputCls + ' pl-9'}
                  value={value.sender_website || ''}
                  onChange={e => set('sender_website', e.target.value)}
                  placeholder="kompetanseforbundet.no" />
              </Field>
            </div>
          </section>

          {/* Betaling */}
          <section>
            <h3 className="text-sm font-semibold uppercase text-gray-500 tracking-wide mb-3">
              Betalingsinformasjon
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Bankkonto (NO-format)" required icon={<Banknote size={16} />}
                help="Vises på faktura som betalingsmottaker. Formater som du vil at det skal rendres (f.eks. 1234.56.78901).">
                <input type="text" className={inputCls + ' pl-9 font-mono'}
                  value={value.bank_account || ''}
                  onChange={e => set('bank_account', e.target.value)}
                  placeholder="0000.00.00000" />
              </Field>
              <Field label="Finance e-post (BCC)" required icon={<Mail size={16} />}
                help="Mottar skjult kopi av hver utgående faktura (for Tripletex/regnskap).">
                <input type="email" className={inputCls + ' pl-9'}
                  value={value.finance_email || ''}
                  onChange={e => set('finance_email', e.target.value)}
                  placeholder="okonomi@kompetanseforbundet.no" />
              </Field>
              <Field label="Forfallsdager" required icon={<Clock size={16} />}
                help="Antall dager fra faktura-dato til forfall. Standard: 30.">
                <input type="number" min={1} max={180} className={inputCls + ' pl-9'}
                  value={value.default_due_days ?? 30}
                  onChange={e => set('default_due_days', Number(e.target.value))} />
              </Field>
            </div>
          </section>

          {/* Pris */}
          <section>
            <h3 className="text-sm font-semibold uppercase text-gray-500 tracking-wide mb-3">
              KI-pakke og pris
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Pakkestørrelse (tokens)" icon={<Package size={16} />}
                help="Antall tokens som utløses per faktura. Standard: 1 000 000.">
                <input type="number" min={100000} step={100000} className={inputCls + ' pl-9'}
                  value={value.topup_tokens_per_invoice ?? 1000000}
                  onChange={e => set('topup_tokens_per_invoice', Number(e.target.value))} />
              </Field>
              <Field label="Pris per pakke (NOK)"
                help="Pris medlemsorganisasjonen betaler per topup-pakke. Standard: 150 NOK (0,15 øre per 1k tokens).">
                <input type="number" min={0} step={10} className={inputCls}
                  value={value.topup_price_nok ?? 150}
                  onChange={e => set('topup_price_nok', Number(e.target.value))} />
              </Field>
            </div>
          </section>

          {/* Notat */}
          <section>
            <h3 className="text-sm font-semibold uppercase text-gray-500 tracking-wide mb-3">
              Merknad på faktura
            </h3>
            <Field label="Fri-tekst som rendres under betalingsboksen" icon={<FileText size={16} />}>
              <textarea rows={2} className={inputCls + ' pl-9'}
                value={value.notes_on_invoice || ''}
                onChange={e => set('notes_on_invoice', e.target.value)}
                placeholder="Betaling merkes med fakturanummer. Spørsmål rettes til okonomi@kompetanseforbundet.no." />
            </Field>
          </section>

          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-xs text-gray-500">
              {serverUpdatedAt
                ? `Sist oppdatert: ${new Date(serverUpdatedAt).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })}`
                : 'Ikke lagret ennå'}
            </p>
            <button type="submit" disabled={saving}
              className="px-6 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary disabled:opacity-50 flex items-center gap-2">
              {saving ? (
                <><Loader2 className="h-5 w-5 animate-spin" /> Lagrer…</>
              ) : (
                <><Save className="h-5 w-5" /> Lagre fakturaoppsett</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent text-sm';

function Field({
  label, required, icon, help, children,
}: {
  label: string; required?: boolean; icon?: React.ReactNode; help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            {icon}
          </span>
        )}
        {children}
      </div>
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );
}
